/**
 * Operator admin panel for scheduling monthly reports to homeowners.
 * Visible to service provider org admins/owners on the property dashboard.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Send, Clock, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface ReportHistoryRow {
  id: string;
  period_start: string;
  period_end: string;
  recipient_emails: string[];
  status: string;
  sent_at: string;
}

interface CustomerReportSettingsProps {
  propertyId: string;
}

export function CustomerReportSettings({ propertyId }: CustomerReportSettingsProps) {
  const { organization } = useApp();
  const [enabled, setEnabled] = useState(false);
  const [sendDay, setSendDay] = useState(1);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [history, setHistory] = useState<ReportHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);

      // Fetch subscription
      const { data: sub } = await supabase
        .from('customer_report_subscriptions')
        .select('id, enabled, send_day_of_month')
        .eq('property_id', propertyId)
        .eq('report_type', 'monthly')
        .maybeSingle();

      if (cancelled) return;

      if (sub) {
        setSubscriptionId(sub.id);
        setEnabled(sub.enabled);
        setSendDay(sub.send_day_of_month);
      } else {
        setSubscriptionId(null);
        setEnabled(false);
        setSendDay(1);
      }

      // Fetch history
      const { data: hist } = await supabase
        .from('customer_report_history')
        .select('id, period_start, period_end, recipient_emails, status, sent_at')
        .eq('property_id', propertyId)
        .order('sent_at', { ascending: false })
        .limit(10);

      if (cancelled) return;
      setHistory(hist ?? []);
      setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, [propertyId]);

  const save = async (newEnabled: boolean, newDay: number) => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !organization) throw new Error('Not authenticated');

      // Compute next_send_at
      const now = new Date();
      let nextSend = new Date(now.getFullYear(), now.getMonth() + 1, newDay);
      if (now.getDate() >= newDay) {
        nextSend = new Date(now.getFullYear(), now.getMonth() + 2, newDay);
      }

      if (subscriptionId) {
        const { error } = await supabase
          .from('customer_report_subscriptions')
          .update({
            enabled: newEnabled,
            send_day_of_month: newDay,
            next_send_at: newEnabled ? nextSend.toISOString() : null,
          })
          .eq('id', subscriptionId);
        if (error) throw error;
      } else if (newEnabled) {
        const { data: newSub, error } = await supabase
          .from('customer_report_subscriptions')
          .insert({
            property_id: propertyId,
            organization_id: organization.id,
            enabled: true,
            send_day_of_month: newDay,
            next_send_at: nextSend.toISOString(),
            created_by: user.id,
          })
          .select('id')
          .single();
        if (error) throw error;
        setSubscriptionId(newSub.id);
      }

      setEnabled(newEnabled);
      setSendDay(newDay);
      toast.success('Report settings saved');
    } catch (err) {
      console.error('Error saving report settings:', err);
      toast.error('Failed to save report settings');
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    if (!subscriptionId) return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-monthly-reports', {
        body: { subscription_id: subscriptionId },
      });
      if (error) throw error;
      const result = data as { sent?: number; results?: { status: string; error?: string }[] };
      if (result.sent && result.sent > 0) {
        toast.success('Report sent');
      } else {
        const firstResult = result.results?.[0];
        toast.error(`Send failed: ${firstResult?.error || firstResult?.status || 'unknown'}`);
      }
      // Refresh history
      const { data: hist } = await supabase
        .from('customer_report_history')
        .select('id, period_start, period_end, recipient_emails, status, sent_at')
        .eq('property_id', propertyId)
        .order('sent_at', { ascending: false })
        .limit(10);
      setHistory(hist ?? []);
    } catch (err) {
      console.error('Error sending report:', err);
      toast.error('Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const now = new Date();
      const periodEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      const periodStart = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), 1);

      const { data, error } = await supabase.rpc('generate_monthly_report_data', {
        p_property_id: propertyId,
        p_period_start: periodStart.toISOString().slice(0, 10),
        p_period_end: periodEnd.toISOString().slice(0, 10),
      });
      if (error) throw error;

      // Dynamically import the template renderer for preview
      // We can't import the Deno template in browser, so render a simplified version
      setPreviewHtml(JSON.stringify(data, null, 2));
      toast.success('Preview generated — see data below');
    } catch (err) {
      console.error('Error generating preview:', err);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-4">
          <div className="h-16 rounded bg-muted/30 animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-1.5">
          <Mail className="h-4 w-4" />
          Customer Reports
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Toggle */}
        <div className="flex items-center justify-between">
          <Label htmlFor="report-toggle" className="text-sm">
            Send monthly summary to homeowner
          </Label>
          <Switch
            id="report-toggle"
            checked={enabled}
            onCheckedChange={(val) => save(val, sendDay)}
            disabled={saving}
          />
        </div>

        {/* Day picker */}
        {enabled && (
          <div className="flex items-center gap-3">
            <Label className="text-sm text-muted-foreground whitespace-nowrap">Send on day</Label>
            <Select
              value={String(sendDay)}
              onValueChange={(val) => save(enabled, parseInt(val))}
              disabled={saving}
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">of each month</span>
          </div>
        )}

        {/* Actions */}
        {enabled && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={preview}
              disabled={previewing}
            >
              <Eye className="w-3 h-3 mr-1" />
              {previewing ? 'Loading...' : 'Preview'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={sendNow}
              disabled={sending || !subscriptionId}
            >
              <Send className="w-3 h-3 mr-1" />
              {sending ? 'Sending...' : 'Send Now'}
            </Button>
          </div>
        )}

        {/* Preview data */}
        {previewHtml && (
          <div className="max-h-64 overflow-auto rounded border border-border bg-muted/20 p-3">
            <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono">
              {previewHtml}
            </pre>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">
              Recent Reports
            </p>
            <div className="space-y-1.5">
              {history.map((h) => (
                <div
                  key={h.id}
                  className="flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-muted/30"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    <span>
                      {new Date(h.period_start).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </span>
                    <span className="text-muted-foreground">
                      to {h.recipient_emails.join(', ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${h.status === 'sent' ? 'text-green-400 border-green-400/30' : 'text-destructive border-destructive/30'}`}
                    >
                      {h.status}
                    </Badge>
                    <span className="text-muted-foreground">
                      {formatDistanceToNow(new Date(h.sent_at), { addSuffix: true })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
